/** Admin tab: edit the event emails (PA and SA versions) and look through the outbox. */
import { api } from "../api";
import { on } from "../state";
import { $, esc, fmtDateTime, openSheet, edHead, toast } from "../ui";

type Tpl = {
  kind: string;
  audience: "PA" | "SA";
  name: string;
  when: string;
  buttons: boolean;
  subject: string;
  body: string;
  custom: boolean;
  defaults: { subject: string; body: string };
};
export type OutboxRow = {
  id: number;
  to: string;
  name: string | null;
  kind: string;
  subject: string;
  status: string;
  error: string | null;
  createdAt: number;
  sentAt: number | null;
  event: string | null;
};

const E: {
  from: string;
  templates: Tpl[] | null;
  kind: string;
  aud: "PA" | "SA";
  outbox: OutboxRow[] | null;
  status: string;
  loading: boolean;
} = { from: "", templates: null, kind: "invite", aud: "PA", outbox: null, status: "", loading: false };

const FIELDS: [string, string][] = [
  ["{date}", "Event date"],
  ["{directions}", "Location directions"],
  ["{end_time}", "End time"],
  ["{event}", "Event name"],
  ["{first}", "First name"],
  ["{last}", "Last name"],
  ["{location}", "Location address"],
  ["{notes}", "Event notes"],
  ["{shadowing}", 'Adds " (Shadowing)" for shadows'],
  ["{time}", "Start time"],
];

const STATUS: Record<string, [string, string]> = {
  dropped: ["Dropped", "warn"],
  failed: ["Failed", "red"],
  held: ["Held (Pre-Launch)", ""],
  queued: ["Queued (Maintenance)", "blue"],
  sending: ["Sending", "blue"],
  sent: ["Sent", "ok"],
};

const KIND_NAMES: Record<string, string> = {
  email_change_code: "Email Change Code",
  email_changed_notice: "Email Changed",
  signin_code: "Sign-In Code",
  signin_instructions: "Sign-In Instructions",
};
const kindName = (k: string) => KIND_NAMES[k] || E.templates?.find((t) => t.kind === k)?.name || k.replace(/_/g, " ");

async function loadTemplates() {
  try {
    const r = await api<{ from: string; templates: Tpl[] }>("/api/emails/templates");
    E.from = r.from;
    E.templates = r.templates;
  } catch (e) {
    toast((e as Error).message);
  }
  const box = $("#emtpl");
  if (box) box.innerHTML = templatesBody();
}

async function loadOutbox() {
  if (E.loading) return;
  E.loading = true;
  try {
    E.outbox = await api<OutboxRow[]>(`/api/emails${E.status ? `?status=${E.status}` : ""}`);
  } catch (e) {
    toast((e as Error).message);
  }
  E.loading = false;
  const box = $("#emout");
  if (box) box.innerHTML = outboxBody();
}

const current = () => E.templates!.find((t) => t.kind === E.kind && t.audience === E.aud)!;

function templatesBody() {
  if (!E.templates) return '<p class="muted">Loading…</p>';
  const t = current();
  const kinds = [...new Map(E.templates.map((x) => [x.kind, x.name])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  return `<div class="row wrap" style="gap:8px 10px;align-items:center">
      <select class="f" style="width:auto;flex:1 1 220px;max-width:340px" data-change="em-kind" aria-label="Email">${kinds
        .map(([k, n]) => `<option value="${k}" ${k === E.kind ? "selected" : ""}>${esc(n)}</option>`)
        .join("")}</select>
      <div class="seg" role="group" aria-label="Version">${(["PA", "SA"] as const)
        .map((a) => `<button class="${E.aud === a ? "on" : ""}" data-act="em-aud" data-aud="${a}">${a === "PA" ? "Parent Ambassadors" : "Student Ambassadors"}</button>`)
        .join("")}</div>
      ${t.custom ? '<span class="chip blue">Edited</span>' : '<span class="chip">Default Wording</span>'}
    </div>
    <p class="muted small" style="margin:0">${esc(t.when)}${t.buttons ? " It has I'll Be There and I Can't Make It buttons under the message." : ""}</p>
    <div><label class="fld">From</label><div class="small" style="padding:6px 0">${esc(E.from)}</div></div>
    <div><label class="fld" for="em-subject">Subject</label><input type="text" id="em-subject" maxlength="200" value="${esc(t.subject)}"></div>
    <div><label class="fld" for="em-body">Message</label><textarea id="em-body" rows="12" maxlength="5000">${esc(t.body)}</textarea></div>
    <details><summary class="small" style="cursor:pointer;font-weight:700">Merge Fields</summary><div class="grid g2 small" style="gap:4px 16px;margin-top:8px">${FIELDS.map(
      ([f, d]) => `<div><code>${f}</code> <span class="muted">${esc(d)}</span></div>`,
    ).join("")}</div></details>
    <div class="row wrap" style="gap:8px">${t.custom ? '<button class="btn ghost sm" data-act="em-reset">Reset to Default</button>' : ""}<button class="btn sm" style="margin-left:auto" data-act="em-save">Save</button></div>`;
}

function statusChip(s: string) {
  const [l, c] = STATUS[s] || [s, ""];
  return `<span class="chip ${c}">${esc(l)}</span>`;
}

export function outboxTable(rows: OutboxRow[], withDelete: boolean) {
  if (!rows.length) return '<p class="muted">No emails.</p>';
  return `<div class="tablewrap"><table><thead><tr><th>Created</th><th>To</th><th>Email</th><th>Status</th>${withDelete ? "<th></th>" : ""}</tr></thead><tbody>${rows
    .map(
      (m) => `<tr${withDelete ? ` class="click" tabindex="0" data-act="em-open" data-id="${m.id}"` : ""}><td class="small">${fmtDateTime(m.createdAt)}</td>
      <td>${m.name ? `<b>${esc(m.name)}</b><div class="muted small">${esc(m.to)}</div>` : esc(m.to)}</td>
      <td><div>${esc(m.subject)}</div><div class="muted small">${esc(kindName(m.kind))}${m.event ? ` · ${esc(m.event)}` : ""}</div></td>
      <td>${statusChip(m.status)}${m.status === "failed" && m.error ? `<div class="muted small">${esc(m.error)}</div>` : ""}</td>
      ${withDelete ? `<td>${["held", "queued"].includes(m.status) ? `<button class="btn ghost sm" data-act="em-del" data-id="${m.id}">Delete</button>` : ""}</td>` : ""}</tr>`,
    )
    .join("")}</tbody></table></div>`;
}

function outboxBody() {
  if (!E.outbox) return '<p class="muted">Loading…</p>';
  return outboxTable(E.outbox, true);
}

export function emailsTab() {
  if (!E.templates) void loadTemplates();
  if (!E.outbox) void loadOutbox();
  return `<div class="stack">
    <div class="card stack" style="gap:12px"><h2>Event Emails</h2><div class="stack" style="gap:12px" id="emtpl">${templatesBody()}</div></div>
    <div class="card stack" style="gap:12px"><div class="row between wrap" style="gap:8px"><h2>Outbox</h2>
      <select class="f" style="width:auto" data-change="em-status" aria-label="Status"><option value="">All Emails</option>${Object.entries(STATUS)
        .sort((a, b) => a[1][0].localeCompare(b[1][0]))
        .map(([k, [l]]) => `<option value="${k}" ${E.status === k ? "selected" : ""}>${esc(l)}</option>`)
        .join("")}</select></div>
      <p class="muted small" style="margin:0">Until the site goes live, emails to anyone but Peter are held and never sent. During maintenance, emails wait in the queue and go out when it ends.</p>
      <div id="emout">${outboxBody()}</div></div></div>`;
}

const rerender = () => {
  const box = $("#emtpl");
  if (box) box.innerHTML = templatesBody();
};

on({
  "em-aud": (el) => {
    E.aud = el.dataset.aud as "PA" | "SA";
    rerender();
  },
  "em-save": async () => {
    try {
      const r = await api<{ from: string; templates: Tpl[] }>(`/api/emails/templates/${E.kind}/${E.aud}`, {
        method: "PUT",
        body: { subject: ($("#em-subject") as HTMLInputElement).value, body: ($("#em-body") as HTMLTextAreaElement).value },
      });
      E.templates = r.templates;
      rerender();
      toast("Saved.");
    } catch (e) {
      toast((e as Error).message);
    }
  },
  "em-reset": async () => {
    try {
      const r = await api<{ from: string; templates: Tpl[] }>(`/api/emails/templates/${E.kind}/${E.aud}`, { method: "DELETE" });
      E.templates = r.templates;
      rerender();
      toast("Back to the default wording.");
    } catch (e) {
      toast((e as Error).message);
    }
  },
  "em-del": async (el) => {
    try {
      await api(`/api/emails/${el.dataset.id}`, { method: "DELETE" });
      E.outbox = E.outbox!.filter((m) => m.id !== Number(el.dataset.id));
      $("#emout")!.innerHTML = outboxBody();
      toast("Deleted. It won't be sent.");
    } catch (e) {
      toast((e as Error).message);
    }
  },
  "em-open": async (el) => {
    try {
      const m = await api<OutboxRow & { text: string; hasInvite: boolean }>(`/api/emails/${el.dataset.id}`);
      openSheet(`${edHead(esc(m.subject), "Email")}<div class="edbody">
        <div class="small"><b>To:</b> ${esc(m.name ? `${m.name} <${m.to}>` : m.to)}</div>
        <div class="small"><b>Created:</b> ${fmtDateTime(m.createdAt)}${m.sentAt ? ` · <b>Sent:</b> ${fmtDateTime(m.sentAt)}` : ""}</div>
        <div>${statusChip(m.status)}${m.hasInvite ? ' <span class="chip blue">Calendar Invite Attached</span>' : ""}</div>
        <div class="card small" style="white-space:pre-wrap">${esc(m.text)}</div></div>
        <div class="edfoot"><button class="btn ghost" data-act="close-sheet">Close</button></div>`);
    } catch (e) {
      toast((e as Error).message);
    }
  },
  "em-kind": (el) => {
    E.kind = (el as HTMLSelectElement).value;
    rerender();
  },
  "em-status": (el) => {
    E.status = (el as HTMLSelectElement).value;
    E.outbox = null;
    $("#emout")!.innerHTML = outboxBody();
    void loadOutbox();
  },
});
